import type { SVGProps } from 'react';
import { useTheme } from '@mui/material';
import { ACCENT_COLOR, THEME_COLOR } from '@app/shared';
import {
  BRAND_MARK_GLYPH_COMPACT,
  BRAND_MARK_GLYPH_STANDARD,
  BRAND_MARK_PLATE_COMPACT,
  BRAND_MARK_PLATE_RADIUS,
  BRAND_MARK_PLATE_STANDARD,
  BRAND_MARK_VIEWBOX,
} from './brandMarkPaths.generated';

/**
 * The brand mark, "the path to the sun", as inline SVG.
 *
 * A white road in perspective, wide at the bottom and winding up through two
 * bends toward a sun in `ACCENT_COLOR`: the user's path of health evolution
 * toward a goal. The geometry is NOT written here: it is generated by
 * `apps/web/scripts/generate-icons.py` into `brandMarkPaths.generated.ts`
 * from the same polygon that paints the PNG icons and writes the SVGs, so this
 * component cannot drift from them. Change the mark there and re-run it.
 *
 * Two geometries: the standard one (perspective taper) and a compact one
 * (uniform-width road, larger sun gap) that stays legible at favicon sizes.
 * `compact` defaults to `size < 32`.
 *
 * Colours:
 * - `plate`: the road is white and the sun is the accent. The plate is
 *   `palette.primary.main` in light mode (which IS `THEME_COLOR`), and the
 *   brand teal `THEME_COLOR` in dark mode, NOT the dark scheme's lighter
 *   primary: the logo is a brand-fixed object, like the app icon on a home
 *   screen, and does not re-tone with the scheme.
 * - `glyph`: no plate. The road is `currentColor` (it inherits the text colour
 *   of its parent) and the sun stays the accent.
 *
 * `ACCENT_COLOR` is a LOGO-ONLY colour: it appears here and nowhere in the UI.
 *
 * Purely decorative by default: callers that place it beside the wordmark
 * pass `aria-hidden`, which spreads onto the `<svg>`. A caller that renders it
 * ALONE as the product's identity passes `role="img"` and an `aria-label`
 * instead.
 */
export interface BrandMarkProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  /** Rendered width and height in CSS pixels. */
  size?: number;
  /**
   * `plate`: the road and sun on the teal rounded square — the app icon.
   * `glyph`: the road (in `currentColor`) and the sun alone, for inline use.
   */
  variant?: 'plate' | 'glyph';
  /**
   * Use the small-size geometry (uniform-width road, larger gap). Defaults to
   * `true` below 32px, where the perspective taper would thin to slivers.
   */
  compact?: boolean;
}

export function BrandMark({
  size = 28,
  variant = 'plate',
  compact = size < 32,
  ...rest
}: BrandMarkProps) {
  const theme = useTheme();
  const isPlate = variant === 'plate';

  const geometry = isPlate
    ? compact
      ? BRAND_MARK_PLATE_COMPACT
      : BRAND_MARK_PLATE_STANDARD
    : compact
      ? BRAND_MARK_GLYPH_COMPACT
      : BRAND_MARK_GLYPH_STANDARD;

  const plateColor = theme.palette.mode === 'dark' ? THEME_COLOR : theme.palette.primary.main;
  const roadColor = isPlate ? theme.palette.common.white : 'currentColor';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${BRAND_MARK_VIEWBOX} ${BRAND_MARK_VIEWBOX}`}
      focusable="false"
      {...rest}
    >
      {isPlate && (
        <rect
          width={BRAND_MARK_VIEWBOX}
          height={BRAND_MARK_VIEWBOX}
          rx={BRAND_MARK_PLATE_RADIUS}
          fill={plateColor}
        />
      )}
      <path d={geometry.road} fill={roadColor} />
      <circle cx={geometry.sun.cx} cy={geometry.sun.cy} r={geometry.sun.r} fill={ACCENT_COLOR} />
    </svg>
  );
}
